import React, { useEffect, useState, useCallback } from "react";
import { Search, Shield, ChefHat, User } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { Spinner, EmptyState } from "../../components/common/UI";
import toast from "react-hot-toast";

const ROLE_ICONS = { admin: Shield, restaurant: ChefHat, user: User };
const ROLE_COLORS = { admin: "bg-red-100 text-red-700", restaurant: "bg-blue-100 text-blue-700", user: "bg-gray-100 text-gray-700" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const fetch = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("users").select("*").order("created_at", { ascending: false });
    if (search) query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%`);
    if (roleFilter !== "all") query = query.eq("role", roleFilter);
    const { data } = await query;
    setUsers(data || []);
    setLoading(false);
  }, [search, roleFilter]);

  useEffect(() => {
    const t = setTimeout(fetch, 300);
    return () => clearTimeout(t);
  }, [fetch]);

  const updateRole = async (userId, newRole) => {
    const { error } = await supabase.from("users").update({ role: newRole }).eq("id", userId);
    if (error) { toast.error("Failed to update role"); return; }
    setUsers((p) => p.map((u) => u.id === userId ? { ...u, role: newRole } : u));
    toast.success(`Role updated to ${newRole}`);
  };

  return (
    <div className="space-y-6 page-enter">
      <div>
        <h1 className="font-display text-2xl font-bold text-gray-900">Users</h1>
        <p className="text-gray-500 text-sm">{users.length} user(s) found</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." className="input-field pl-10 py-2.5" />
        </div>
        <div className="flex gap-2">
          {["all", "user", "restaurant", "admin"].map((r) => (
            <button key={r} onClick={() => setRoleFilter(r)}
              className={`px-4 py-2.5 rounded-xl border text-sm font-medium capitalize transition-colors ${roleFilter === r ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-600 hover:border-orange-300"}`}>
              {r}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : users.length === 0 ? (
        <EmptyState icon="👥" title="No users found" />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100 bg-gray-50">
                  <th className="px-5 py-3 text-left">User</th>
                  <th className="px-5 py-3 text-left">Phone</th>
                  <th className="px-5 py-3 text-left">Role</th>
                  <th className="px-5 py-3 text-left">Joined</th>
                  <th className="px-5 py-3 text-left">Change Role</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map((u) => {
                  const Icon = ROLE_ICONS[u.role] || User;
                  return (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-600 text-sm">
                            {(u.name || u.email || "U")[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{u.name || "—"}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-600">{u.phone || "—"}</td>
                      <td className="px-5 py-4">
                        <span className={`badge ${ROLE_COLORS[u.role] || ""} flex items-center gap-1 w-fit`}>
                          <Icon className="w-3 h-3" /> {u.role}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-gray-400">{new Date(u.created_at).toLocaleDateString("en-PK")}</td>
                      <td className="px-5 py-4">
                        <select
                          value={u.role}
                          onChange={(e) => updateRole(u.id, e.target.value)}
                          className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white text-gray-700"
                        >
                          <option value="user">User</option>
                          <option value="restaurant">Restaurant</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
